import { useRef, useState } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  Bookmark,
  ChevronDown,
  ChevronRight,
  Database,
  FileText,
  HardDrive,
  Play,
  RefreshCw,
  Trash2,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import { closeSync, runsPage } from "@/api/runs";
import {
  deleteRunSource,
  importDiskUrlList,
  listDiskUrlLists,
  listRunSources,
  uploadRunSourceWithProgress,
} from "@/api/run-sources";
import type { RunSource } from "@/api/types";
import { ActiveRunsPanel } from "@/components/active-runs-panel";
import { BackgroundActivityCard } from "@/components/background-activity-card";
import { Page } from "@/components/page";
import { StatusBadge } from "@/components/status-badge";
import { RelativeTime } from "@/components/relative-time";
import { EmptyState } from "@/components/empty-state";
import { RunModal } from "@/components/run-modal";

export function RunsListPage() {
  const qc = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [showProgress, setShowProgress] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Which source's RunModal is open. The modal handles check selection,
  // force-rescan, and the actual run-launch mutation itself.
  const [modalSource, setModalSource] = useState<RunSource | null>(null);

  const runs = useInfiniteQuery({
    queryKey: ["runs"],
    queryFn: ({ pageParam }) => runsPage(pageParam ?? null, { limit: 50 }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
  });

  const sources = useQuery({
    queryKey: ["run-sources"],
    queryFn: listRunSources,
  });

  const [diskOpen, setDiskOpen] = useState(false);
  const [diskFilter, setDiskFilter] = useState("");
  const diskLists = useQuery({
    queryKey: ["run-sources-disk"],
    queryFn: listDiskUrlLists,
    enabled: diskOpen,
  });
  const filteredDiskLists = (diskLists.data ?? []).filter((f) => {
    const q = diskFilter.trim().toLowerCase();
    return !q || f.rel_path.toLowerCase().includes(q);
  });
  const importDisk = useMutation({
    mutationFn: (relPath: string) => importDiskUrlList(relPath),
    onSuccess: (created) => {
      toast.success(
        `Imported "${created.name}" (${created.line_count.toLocaleString()} URLs)`,
      );
      qc.invalidateQueries({ queryKey: ["run-sources"] });
      qc.invalidateQueries({ queryKey: ["run-sources-disk"] });
    },
    onError: (err) => {
      toast.error(`Import failed: ${(err as Error).message}`);
    },
  });

  const sync = useMutation({
    mutationFn: ({ full }: { full: boolean }) => closeSync(full),
    onSuccess: (r) => {
      if (r.backgrounded) {
        toast.info("Full sync started — UI will lock until done.");
      } else {
        toast.success(
          r.was_full ? `Full sync · ${r.count} leads` : `Synced ${r.count} leads`,
        );
      }
    },
  });

  const remove = useMutation({
    mutationFn: (sourceId: string) => deleteRunSource(sourceId),
    onSuccess: () => {
      toast.success("Source removed");
      qc.invalidateQueries({ queryKey: ["run-sources"] });
    },
  });

  /** Save the picked file as a source. Run-start is now a separate click. */
  const onFilePicked = async (picked: File | null) => {
    if (!picked) return;
    setUploading(true);
    setUploadProgress(null);
    setShowProgress(false);
    // Only show the progress UI if the upload is still going at 500 ms —
    // small files complete in a flash and the indicator would just blink.
    const showTimer = window.setTimeout(() => setShowProgress(true), 500);
    try {
      const created = await uploadRunSourceWithProgress(
        picked.name,
        picked,
        (frac) => setUploadProgress(frac),
      );
      toast.success(
        `Saved "${created.name}" (${created.line_count.toLocaleString()} URLs) — choose how to run it below`,
      );
      qc.invalidateQueries({ queryKey: ["run-sources"] });
    } catch (err) {
      toast.error(`Upload failed: ${(err as Error).message}`);
    } finally {
      window.clearTimeout(showTimer);
      setUploading(false);
      setUploadProgress(null);
      setShowProgress(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const items = runs.data?.pages.flatMap((p) => p.items) ?? [];
  const sourceItems = sources.data ?? [];

  return (
    <Page
      title="Runs"
      actions={
        <button
          className="btn-ghost text-xs"
          onClick={(e) => {
            const full = e.shiftKey;
            if (
              full &&
              !confirm(
                "Full Close sync pulls every lead (~15K). The UI will lock " +
                  "for a few minutes. Continue?",
              )
            ) {
              return;
            }
            sync.mutate({ full });
          }}
          disabled={sync.isPending}
          title="Sync Close (incremental). Auto-runs at every run start; click only if you want to force one. Shift-click for a full pull."
        >
          <RefreshCw
            className={sync.isPending ? "h-3.5 w-3.5 animate-spin" : "h-3.5 w-3.5"}
          />
          Sync Close
        </button>
      }
    >
      <ActiveRunsPanel />
      <BackgroundActivityCard />

      {/* Import URL lists dropped on the host filesystem under
          data/url_lists/YYYY-MM-DD/*.txt. Lazy — only fetches when expanded. */}
      <section className="card overflow-hidden">
        <button
          type="button"
          className="w-full px-4 py-3 border-b border-border text-sm font-semibold flex items-center justify-between gap-3 hover:bg-subtle/30 transition-colors"
          onClick={() => setDiskOpen((v) => !v)}
        >
          <span className="flex items-center gap-2">
            {diskOpen ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            <HardDrive className="h-3.5 w-3.5 text-muted" />
            Import from disk
          </span>
          <span className="text-xs text-muted font-normal">
            {diskLists.data?.length
              ? `${diskLists.data.length.toLocaleString()} file${
                  diskLists.data.length === 1 ? "" : "s"
                } available`
              : null}
          </span>
        </button>
        {diskOpen ? (
          diskLists.isLoading ? (
            <div className="px-4 py-3 text-xs text-muted">Loading…</div>
          ) : (diskLists.data ?? []).length === 0 ? (
            <EmptyState
              title="No URL lists on disk"
              hint="Drop YYYY-MM-DD/*.txt files into ./data/url_lists/ to import them as run-sources."
            />
          ) : (
            <>
              <div className="px-4 py-2 border-b border-border">
                <input
                  type="text"
                  value={diskFilter}
                  onChange={(e) => setDiskFilter(e.target.value)}
                  placeholder="Filter by date or filename (e.g. domains-de)…"
                  className="w-full text-sm px-2 py-1 bg-transparent border border-border rounded focus:outline-none focus:border-accent"
                />
                {diskFilter ? (
                  <div className="mt-1 text-[11px] text-muted tabular-nums">
                    {filteredDiskLists.length.toLocaleString()} of{" "}
                    {(diskLists.data ?? []).length.toLocaleString()} files
                  </div>
                ) : null}
              </div>
              {filteredDiskLists.length === 0 ? (
                <div className="px-4 py-3 text-xs text-muted">
                  No files match the filter.
                </div>
              ) : (
                <div className="overflow-x-auto">
                <table className="w-full min-w-[40rem] text-sm">
                  <thead>
                    <tr className="text-xs text-muted">
                      <th className="px-4 py-2 text-left font-medium">Date</th>
                      <th className="px-4 py-2 text-left font-medium">File</th>
                      <th className="px-4 py-2 text-right font-medium">URLs</th>
                      <th className="px-4 py-2 text-right font-medium">Size</th>
                      <th className="px-4 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredDiskLists.map((f) => (
                      <tr key={f.rel_path} className="hover:bg-subtle/40">
                        <td className="px-4 py-2 font-mono text-xs text-muted">
                          {f.date}
                        </td>
                        <td className="px-4 py-2 font-mono text-xs">
                          {f.filename}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums text-muted text-xs">
                          {f.line_count.toLocaleString()}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums text-muted text-xs">
                          {formatBytes(f.byte_count)}
                        </td>
                        <td className="px-4 py-2 text-right whitespace-nowrap">
                          <button
                            className="btn-ghost text-xs"
                            onClick={() => importDisk.mutate(f.rel_path)}
                            disabled={importDisk.isPending}
                            title="Import this file into a kind=file saved source"
                          >
                            <Upload className="h-3.5 w-3.5" /> Import
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
            </>
          )
        ) : null}
      </section>

      {/* Saved sources — upload trigger lives in the header */}
      <section className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-border text-sm font-semibold flex items-center justify-between gap-3">
          <span>Saved sources</span>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted tabular-nums">
              {sourceItems.length.toLocaleString()} saved
            </span>
            <label className="inline-block">
              <input
                ref={fileInputRef}
                type="file"
                accept=".txt,text/plain"
                className="hidden"
                disabled={uploading}
                onChange={(e) => onFilePicked(e.target.files?.[0] ?? null)}
              />
              <span
                className={
                  uploading
                    ? "btn-ghost text-xs opacity-60 pointer-events-none"
                    : "btn-ghost text-xs cursor-pointer"
                }
                title="Save a .txt file (one URL per line) as a re-runnable source. Already-extracted leads are skipped."
              >
                <Upload className="h-3.5 w-3.5" />
                {uploading ? "Uploading…" : "Upload"}
              </span>
            </label>
          </div>
        </div>
        {uploading && showProgress ? (
          <div className="px-4 py-3 border-b border-border">
            <div className="flex items-center gap-2 text-xs text-muted mb-1">
              <RefreshCw className="h-3.5 w-3.5 animate-spin" />
              <span>
                Uploading…
                {uploadProgress != null
                  ? ` ${Math.round(uploadProgress * 100)}%`
                  : ""}
              </span>
            </div>
            <div className="h-1.5 bg-subtle rounded overflow-hidden">
              <div
                className="h-full bg-accent transition-[width] duration-150"
                style={{
                  width:
                    uploadProgress != null
                      ? `${Math.round(uploadProgress * 100)}%`
                      : "100%",
                }}
              />
            </div>
          </div>
        ) : null}
        {sourceItems.length === 0 && !sources.isLoading ? (
          <EmptyState
            title="No saved sources yet"
            hint='Click "Upload" above to save a .txt file (one URL per line).'
          />
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[44rem] text-sm">
            <thead>
              <tr className="text-xs text-muted">
                <th className="px-4 py-2 text-left font-medium">Name</th>
                <th className="px-4 py-2 text-right font-medium">URLs</th>
                <th className="px-4 py-2 text-right font-medium">Size</th>
                <th className="px-4 py-2 text-left font-medium">Uploaded</th>
                <th className="px-4 py-2 text-left font-medium">Last run</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {sourceItems.map((s) => {
                const isFilter = s.kind === "filter";
                const isSynthetic = s.kind === "virtual";
                // "virtual" for UI styling = anything where there's no
                // file body and the run forces rescan. Both filter and
                // synthetic count. Only the synthetic one refuses delete.
                const virtualUi = isFilter || isSynthetic;
                return (
                  <tr
                    key={s.id}
                    className={
                      virtualUi
                        ? "bg-subtle/30 hover:bg-subtle/60"
                        : "hover:bg-subtle/40"
                    }
                  >
                    <td className="px-4 py-2 font-mono text-xs flex items-center gap-2">
                      {isSynthetic ? (
                        <Database className="h-3.5 w-3.5 text-accent shrink-0" />
                      ) : isFilter ? (
                        <Bookmark className="h-3.5 w-3.5 text-accent shrink-0" />
                      ) : (
                        <FileText className="h-3.5 w-3.5 text-muted shrink-0" />
                      )}
                      <span className={virtualUi ? "text-fg" : ""}>{s.name}</span>
                      {isSynthetic ? (
                        <span className="badge text-[10px] shrink-0 bg-accent/15 text-accent border border-accent/30">
                          virtual
                        </span>
                      ) : isFilter ? (
                        <span
                          className="badge text-[10px] shrink-0 bg-accent/15 text-accent border border-accent/30"
                          title={summarizeFilter(s.filter_spec)}
                        >
                          filter
                        </span>
                      ) : null}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {s.line_count.toLocaleString()}
                      {isFilter ? (
                        <span className="text-muted text-[10px] ml-1">
                          (at save)
                        </span>
                      ) : null}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-muted text-xs">
                      {virtualUi ? "—" : formatBytes(s.byte_count)}
                    </td>
                    <td className="px-4 py-2 text-muted text-xs">
                      {isSynthetic ? "—" : <RelativeTime iso={s.created_at} />}
                    </td>
                    <td className="px-4 py-2 text-muted text-xs">
                      {s.last_used_at ? (
                        s.last_run_id ? (
                          <Link
                            to="/runs/$runId"
                            params={{ runId: s.last_run_id }}
                            className="text-accent hover:underline"
                          >
                            <RelativeTime iso={s.last_used_at} />
                          </Link>
                        ) : (
                          <RelativeTime iso={s.last_used_at} />
                        )
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-4 py-2 text-right space-x-1 whitespace-nowrap">
                      <button
                        className="btn-ghost text-xs"
                        onClick={() => setModalSource(s)}
                        title={
                          isSynthetic
                            ? "Re-evaluate every indexed domain against the picked checks"
                            : isFilter
                              ? "Run the picked checks on every domain matching the saved filter (resolved live)"
                              : "Pick checks + force-rescan, then run"
                        }
                      >
                        <Play className="h-3.5 w-3.5" /> Run…
                      </button>
                      {isSynthetic ? null : (
                        <button
                          className="btn-ghost text-xs"
                          onClick={() => {
                            if (confirm(`Delete saved source "${s.name}"?`)) {
                              remove.mutate(s.id);
                            }
                          }}
                          disabled={remove.isPending}
                          title="Delete saved source"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </section>

      {/* Recent runs */}
      <section className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-border text-sm font-semibold">
          Recent runs
        </div>
        {items.length === 0 && !runs.isLoading ? (
          <EmptyState title="No runs yet" hint="Submit one above to get started." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[30rem] text-sm">
              <thead>
                <tr className="text-xs text-muted">
                  <th className="px-4 py-2 text-left font-medium">Source</th>
                  <th className="px-4 py-2 text-left font-medium">Status</th>
                  <th className="px-4 py-2 text-left font-medium">Started</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map((r) => (
                  <tr key={r.id} className="hover:bg-subtle/40">
                    <td className="px-4 py-2">
                      <Link
                        to="/runs/$runId"
                        params={{ runId: r.id }}
                        className="text-accent hover:underline"
                      >
                        {r.source}
                      </Link>
                    </td>
                    <td className="px-4 py-2"><StatusBadge status={r.status} /></td>
                    <td className="px-4 py-2 text-muted">
                      <RelativeTime iso={r.started_at} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {runs.hasNextPage ? (
          <div className="px-4 py-3 border-t border-border text-center">
            <button
              className="btn-ghost"
              onClick={() => runs.fetchNextPage()}
              disabled={runs.isFetchingNextPage}
            >
              {runs.isFetchingNextPage ? "Loading…" : "Load more"}
            </button>
          </div>
        ) : null}
      </section>

      {modalSource ? (
        <RunModal
          source={modalSource}
          onClose={() => setModalSource(null)}
        />
      ) : null}
    </Page>
  );
}

function summarizeFilter(spec: RunSource["filter_spec"]): string {
  if (!spec) return "filter";
  const bits: string[] = [];
  if (spec.status) bits.push(`status=${spec.status}`);
  if (spec.tag?.length) bits.push(`tag=${spec.tag.join("+")}`);
  if (spec.run_id) bits.push(`run=${spec.run_id.slice(0, 8)}`);
  if (spec.q) bits.push(`q=${spec.q}`);
  return bits.length ? bits.join("  ·  ") : "filter";
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1_048_576).toFixed(1)} MB`;
  return `${(n / 1_073_741_824).toFixed(2)} GB`;
}
